/**
 * Shared platform chip — used on Coming Soon cards and "Now Streaming" rails.
 *
 * Shows where a title can be watched (Netflix / Prime / Hotstar / Sony LIV /
 * ZEE5 / JioCinema / Apple TV+ / etc) or "In Theatres" for theatrical-only
 * movies. Translucent palette stays subordinate to the title typography.
 */
import { Ticket } from "lucide-react";

export const PLATFORM_STYLES = {
  netflix:    { bg: "bg-red-500/15",     border: "border-red-500/40",     text: "text-red-300" },
  prime:      { bg: "bg-sky-500/15",     border: "border-sky-500/40",     text: "text-sky-300" },
  hotstar:    { bg: "bg-blue-500/15",    border: "border-blue-500/40",    text: "text-blue-300" },
  disney:     { bg: "bg-indigo-500/15",  border: "border-indigo-500/40",  text: "text-indigo-300" },
  sonyliv:    { bg: "bg-cyan-500/15",    border: "border-cyan-500/40",    text: "text-cyan-300" },
  zee5:       { bg: "bg-violet-500/15",  border: "border-violet-500/40",  text: "text-violet-300" },
  jiocinema:  { bg: "bg-fuchsia-500/15", border: "border-fuchsia-500/40", text: "text-fuchsia-300" },
  apple:      { bg: "bg-neutral-500/15", border: "border-neutral-500/40", text: "text-neutral-200" },
  hbo:        { bg: "bg-purple-500/15",  border: "border-purple-500/40",  text: "text-purple-300" },
  mx:         { bg: "bg-orange-500/15",  border: "border-orange-500/40",  text: "text-orange-300" },
  hulu:       { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-300" },
  paramount:  { bg: "bg-blue-600/15",    border: "border-blue-600/40",    text: "text-blue-300" },
  peacock:    { bg: "bg-pink-500/15",    border: "border-pink-500/40",    text: "text-pink-300" },
  aha:        { bg: "bg-rose-500/15",    border: "border-rose-500/40",    text: "text-rose-300" },
  eros:       { bg: "bg-pink-500/15",    border: "border-pink-500/40",    text: "text-pink-300" },
  theatre:    { bg: "bg-amber-500/15",   border: "border-amber-500/40",   text: "text-amber-300" },
  lionsgate:  { bg: "bg-yellow-500/15",  border: "border-yellow-500/40",  text: "text-yellow-300" },
  vi:         { bg: "bg-red-600/15",     border: "border-red-600/40",     text: "text-red-300" },
  voot:       { bg: "bg-purple-600/15",  border: "border-purple-600/40",  text: "text-purple-300" },
  sunnxt:     { bg: "bg-orange-600/15",  border: "border-orange-600/40",  text: "text-orange-300" },
  discovery:  { bg: "bg-blue-700/15",    border: "border-blue-700/40",    text: "text-blue-300" },
  shemaroo:   { bg: "bg-rose-600/15",    border: "border-rose-600/40",    text: "text-rose-300" },
  crunchyroll:{ bg: "bg-orange-400/15",  border: "border-orange-400/40",  text: "text-orange-200" },
  youtube:    { bg: "bg-red-600/15",     border: "border-red-600/40",     text: "text-red-300" },
  hoichoi:    { bg: "bg-purple-700/15",  border: "border-purple-700/40",  text: "text-purple-300" },
  mubi:       { bg: "bg-slate-500/15",   border: "border-slate-500/40",   text: "text-slate-200" },
  hungama:    { bg: "bg-amber-600/15",   border: "border-amber-600/40",   text: "text-amber-300" },
  epicon:     { bg: "bg-teal-600/15",    border: "border-teal-600/40",    text: "text-teal-300" },
  manorama:   { bg: "bg-green-600/15",   border: "border-green-600/40",   text: "text-green-300" },
  other:      { bg: "bg-white/[0.06]",   border: "border-white/15",       text: "text-white/70" },
};

// Display-name fallback when only _ott_platform code is present (legacy field).
const FALLBACK_NAMES = {
  netflix:    "Netflix",
  prime:      "Prime Video",
  hotstar:    "JioHotstar",
  sonyliv:    "Sony LIV",
  zee5:       "ZEE5",
  jiocinema:  "JioCinema",
  apple:      "Apple TV+",
  disney:     "Disney+",
  hbo:        "HBO Max",
  mx:         "Amazon MX Player",
  aha:        "aha",
  crunchyroll:"Crunchyroll",
  lionsgate:  "Lionsgate Play",
  vi:         "VI Movies & TV",
  voot:       "Voot",
  sunnxt:     "Sun NXT",
  discovery:  "Discovery+",
  hoichoi:    "Hoichoi",
  youtube:    "YouTube",
  mubi:       "MUBI",
  hungama:    "Hungama Play",
  epicon:     "EPIC ON",
  manorama:   "ManoramaMax",
};

export function resolvePlatform(item) {
  // Prefer the new v1.97 field; fall back to legacy _ott_platform.
  const key = item?.platform_key || item?._ott_platform || null;
  const name = item?.platform_name || (key ? FALLBACK_NAMES[key] : null);
  if (!key || !name) return null;
  return { key, name };
}

export default function PlatformChip({ item, size = "sm" }) {
  const resolved = resolvePlatform(item);
  if (!resolved) return null;
  const style = PLATFORM_STYLES[resolved.key] || PLATFORM_STYLES.other;
  const isTheatre = resolved.key === "theatre";
  const sizeClass =
    size === "xs"
      ? "px-1.5 py-0.5 text-[9.5px]"
      : "px-2 py-0.5 text-[10.5px]";
  return (
    <span
      data-testid={`platform-chip-${resolved.key}`}
      className={`inline-flex items-center gap-1 rounded font-bold border ${style.bg} ${style.border} ${style.text} ${sizeClass} max-w-full truncate`}
      title={isTheatre ? "Theatrical release" : `Watch on ${resolved.name}`}
    >
      {isTheatre && <Ticket className="w-2.5 h-2.5 shrink-0" />}
      <span className="truncate">{resolved.name}</span>
    </span>
  );
}

/**
 * MultiPlatformChips — shows up to 3 platform chips side-by-side.
 * platforms: [{key, name}]  (from watchProvidersCache)
 */
export function MultiPlatformChips({ platforms }) {
  if (!platforms?.length) return null;
  const shown = platforms.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-0.5" data-testid="multi-platform-chips">
      {shown.map((p) => {
        const style = PLATFORM_STYLES[p.key] || PLATFORM_STYLES.other;
        return (
          <span
            key={p.key}
            data-testid={`platform-chip-${p.key}`}
            className={`inline-flex items-center rounded font-bold border px-1.5 py-0.5 text-[9px] ${style.bg} ${style.border} ${style.text}`}
            title={`Watch on ${p.name}`}
          >
            <span className="truncate max-w-[52px]">{p.name}</span>
          </span>
        );
      })}
    </div>
  );
}
